import type { SimulationOptions } from '@rcs/physics';
import type { BuiltPoint } from './course/builder';
import {
  doDodonpaPoints,
  dodonpaPoints,
  eejanaikaPoints,
  formulaRossaPoints,
  fujiyamaPoints,
  takabishaPoints,
} from './course/famous';
import {
  downhillPoints,
  helixCoasterPoints,
  highSpeedRailPoints,
  highlandRailPoints,
  launchCoasterPoints,
  loopCoasterPoints,
} from './course/samples';
import type { CourseInput } from './worker/protocol';

/** 制御点の区間種別（その制御点から次の制御点までの区間）。省略は通常区間。 */
export interface SampleSegment {
  readonly type: 'normal' | 'lift' | 'launch' | 'brake' | 'station';
  /** パラメータ（省略は既定値） */
  readonly params?: Readonly<Record<string, number>>;
}

/** 動作確認用の仮のコースと、その走行条件。 */
export interface SampleCourse {
  readonly course: CourseInput;
  /** 制御点ごとの区間種別（制御点と同じ数。省略はすべて通常） */
  readonly segments?: readonly SampleSegment[];
  readonly simulation?: SimulationOptions;
}

/**
 * 単純な仮のコース（高さ 30 m から谷と丘を下る。区間種別なし）。
 * 画面の確認や E2E テストで使う（URL の `?course=simple`）。
 */
export const SAMPLE_COURSE: CourseInput = {
  points: [
    [0, 30, 0],
    [25, 26, 0],
    [55, 4, 0],
    [85, 2, 6],
    [115, 14, 0],
    [145, 8, -6],
    [175, 3, 0],
    [205, 1, 0],
  ],
  closed: false,
};

/**
 * サンプルコース（B-47）：駅 → リフト → 落下 → キャメルバック 2 つ → 停止ブレーキ。
 * 標準車両の限界値（上下 G 5 / −1.5、横 G 1.8）に収まる。
 */
const CAMELBACK_COURSE: CourseInput = {
  points: [
    [0, 2, 0], // 駅
    [20, 2, 0], // リフト（c2 → c4）
    [70, 40, 0], // 頂上
    [95, 38, 0], // 頂上を越えたら落下
    [130, 6, 0],
    [170, 4, 0],
    [200, 16, 0], // キャメルバック 1
    [235, 7, 0],
    [265, 12, 0], // キャメルバック 2
    [300, 4, 0], // 停止ブレーキ（c10 → c12）
    [340, 4, 0],
    [380, 4, 0],
  ],
  closed: false,
};

/** 発進加速の区間のパラメータ（30 m/s = 108 km/h まで、10 m/s² ≈ 1 G で加速）。 */
const LAUNCH_PARAMS = { targetSpeed: 30, maxAcceleration: 10 } as const;

/** ビルダーで作った制御点の列を、サンプルコースにする。 */
function fromBuilt(points: readonly BuiltPoint[]): SampleCourse {
  return {
    course: {
      points: points.map((point) => point.position),
      banks: points.map((point) => point.bank),
      closed: false,
    },
    segments: points.map((point): SampleSegment => {
      // 発進加速は、指定がなければコースター向けの既定（30 m/s・10 m/s²）
      const params = point.params ?? (point.segment === 'launch' ? LAUNCH_PARAMS : undefined);
      return params === undefined ? { type: point.segment } : { type: point.segment, params };
    }),
  };
}

/**
 * 画面の確認用に、URL の `?course=` で選べる仮のコース。
 *
 * - `default`：サンプルコース（駅・リフト・落下・キャメルバック・ブレーキ）
 * - `launch`・`loop`・`helix`：サンプルプロジェクトの他のコース（ローンチ・ループ・ヘリックス）
 * - `dodonpa`・`do-dodonpa`・`fujiyama`・`eejanaika`・`takabisha`・`formula-rossa`：実在のコースターを
 *   真似たコース（`course/famous.ts`）
 * - `simple`：単純なコース（区間種別なし、警告なし）
 * - `stall`：高さ不足の丘で失速する
 * - `high-g`：深い谷で上下 G が上限を大きく超える
 */
export const SAMPLE_COURSES: Readonly<Record<string, SampleCourse>> = {
  default: {
    course: CAMELBACK_COURSE,
    segments: [
      { type: 'station' },
      { type: 'lift' },
      { type: 'lift' },
      ...Array.from({ length: 6 }, (): SampleSegment => ({ type: 'normal' })),
      { type: 'brake' },
      { type: 'brake' },
      { type: 'normal' },
    ],
  },
  launch: fromBuilt(launchCoasterPoints()),
  loop: fromBuilt(loopCoasterPoints()),
  helix: fromBuilt(helixCoasterPoints()),
  highland: fromBuilt(highlandRailPoints()),
  'high-speed': fromBuilt(highSpeedRailPoints()),
  downhill: fromBuilt(downhillPoints()),
  dodonpa: fromBuilt(dodonpaPoints()),
  'do-dodonpa': fromBuilt(doDodonpaPoints()),
  fujiyama: fromBuilt(fujiyamaPoints()),
  eejanaika: fromBuilt(eejanaikaPoints()),
  takabisha: fromBuilt(takabishaPoints()),
  'formula-rossa': fromBuilt(formulaRossaPoints()),
  simple: { course: SAMPLE_COURSE },
  stall: {
    course: {
      points: [
        [0, 5, 0],
        [40, 5, 0],
        [80, 12, 0],
        [120, 20, 0],
        [160, 20, 0],
        [200, 4, 0],
      ],
      closed: false,
    },
    simulation: { startSpeed: 12, maxTime: 60 },
  },
  'high-g': {
    course: {
      points: [
        [0, 70, 0],
        [25, 62, 0],
        [45, 14, 0],
        [58, 1, 0],
        [68, 1, 0],
        [80, 12, 0],
        [110, 40, 0],
        [150, 36, 0],
      ],
      closed: false,
    },
    simulation: { startSpeed: 2 },
  },
};

/** URL のクエリ文字列から、使う仮のコースを選ぶ。未知の名前は既定のコース。 */
export function pickSampleCourse(search: string): SampleCourse {
  const name = new URLSearchParams(search).get('course') ?? 'default';
  return SAMPLE_COURSES[name] ?? SAMPLE_COURSES.default ?? { course: CAMELBACK_COURSE };
}
